import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import "../styles/OfferDetails.css";
import { fetchOfferDetails, getLayoutById } from "../services/api";
import NotFound from "../components/NotFound";
import DetailCtaArea from "../components/DetailCtaArea";
import { formatCurrency } from "../utils/formatCurrency";
import { Helmet } from "react-helmet";
import InspectionRequestModal from "../components/InspectionRequestModal";
import { formatUrl } from "../utils/config";

const OfferDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(location.state?.offerData);
  const [loading, setLoading] = useState(!location.state?.offerData);
  const [layoutName, setLayoutName] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Fetch layout name if it's a plot offer
    const fetchLayoutName = async () => {
      if (offer?.propertyType === "PLOT" && offer?.plot?.layoutId) {
        try {
          const response = await getLayoutById(offer.plot.layoutId);
          if (response.success && response.payload) {
            setLayoutName(
              response.payload.name ||
                response.payload.title ||
                `Layout ${offer.plot.layoutId}`
            );
          }
        } catch (error) {
          console.error("Failed to fetch layout name:", error);
        }
      }
    };

    if (offer) {
      fetchLayoutName();
    }

    // Only fetch if we don't have the data from state
    if (!location.state?.offerData) {
      const fetchOffer = async () => {
        try {
          const response = await fetchOfferDetails(id);
          setOffer(response);
          // Fetch layout name after getting offer data
          if (response?.propertyType === "PLOT" && response?.plot?.layoutId) {
            try {
              const layoutResponse = await getLayoutById(
                response.plot.layoutId
              );
              if (layoutResponse.success && layoutResponse.payload) {
                setLayoutName(
                  layoutResponse.payload.name ||
                    layoutResponse.payload.title ||
                    `Layout ${response.plot.layoutId}`
                );
              }
            } catch (error) {
              console.error("Failed to fetch layout name:", error);
            }
          }
        } catch (error) {
          console.error("Error fetching offer details:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchOffer();
    }
  }, [id, location.state, offer]);

  // Helper functions to get property data
  const getPropertyData = () => {
    if (offer?.propertyType === "HOUSE") {
      return offer.house;
    } else if (offer?.propertyType === "PLOT") {
      return offer.plot;
    }
    return null;
  };

  const getPropertyType = () => {
    return offer?.propertyType || "Unknown";
  };

  const getLocation = () => {
    const property = getPropertyData();
    if (property) {
      return `${property.district || ""} ${property.lga} ${property.state}`;
    }
    return offer?.propertyLocation || "Location not specified";
  };

  const getPriceDisplay = () => {
    const property = getPropertyData();
    if (!property?.price) return "Price on request";

    const price = parseFloat(property.price);
    if (offer?.discountPercentage) {
      const discountedPrice = price * (1 - offer.discountPercentage / 100);
      return {
        originalPrice: formatCurrency(price),
        discountedPrice: formatCurrency(discountedPrice),
        hasDiscount: true,
      };
    }
    return {
      originalPrice: formatCurrency(price),
      discountedPrice: null,
      hasDiscount: false,
    };
  };

  const getThumbnail = () => {
    const property = getPropertyData();
    if (offer?.propertyType === "HOUSE" && property?.thumbnail) {
      return property.thumbnail;
    } else if (
      offer?.propertyType === "PLOT" &&
      property?.photos &&
      property.photos.length > 0
    ) {
      return property.photos[0];
    }
    return "/assets/img/default-offer.png";
  };

  const getAdditionalPhotos = () => {
    const property = getPropertyData();
    if (offer?.propertyType === "HOUSE" && property?.photos) {
      return property.photos.slice(1, 3);
    } else if (offer?.propertyType === "PLOT" && property?.photos) {
      return property.photos.slice(1, 3);
    }
    return [];
  };

  const createMarkup = (html) => {
    return { __html: html };
  };

  if (loading) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <h3>Offer not found</h3>
              <p>
                The offer you are looking for does not exist or has been
                removed.
              </p>
              <button className="th-btn" onClick={() => navigate("/offers")}>
                Back to Offers
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const property = getPropertyData();
  const priceInfo = getPriceDisplay();
  const additionalPhotos = getAdditionalPhotos();

  return (
    <>
      <Helmet>
        <title>{`${offer.title} - ${getPropertyType()} Offer Details`}</title>
        <meta
          name="description"
          content={`${offer.description} - ${getLocation()}. ${
            property?.sizeSqm
          } sqm available for ${priceInfo.originalPrice}`}
        />
      </Helmet>

      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{offer.title}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/offers">Offers</Link>
              </li>
              <li>{offer.title}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-top space-extra-bottom">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="property-main-image mb-4">
                <img
                  src={formatUrl(getThumbnail())}
                  alt={offer.title}
                  className="img-fluid rounded w-100"
                  style={{ height: "500px", objectFit: "cover" }}
                  onError={() => setImageError(true)}
                />
              </div>

              <div className="property-details mb-5">
                <h2 className="h3 mb-3">{offer.title}</h2>
                <p className="text-muted mb-4">
                  <i className="fas fa-map-marker-alt text-primary me-2"></i>
                  {getLocation()}
                </p>

                <div className="property-features mb-4">
                  <div className="row g-3">
                    <div className="col-md-3 col-6">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-ruler-combined fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">{property?.sizeSqm} sqm</h6>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-tag fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">{offer.code}</h6>
                        <small className="text-muted">Offer Code</small>
                      </div>
                    </div>
                    {offer?.propertyType === "HOUSE" && (
                      <div className="col-md-3 col-6">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-bed fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{property?.bedRooms}</h6>
                          <small className="text-muted">Bedrooms</small>
                        </div>
                      </div>
                    )}
                    {offer?.propertyType === "PLOT" && (
                      <div className="col-md-3 col-6">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-file-signature fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{property?.plotNumber}</h6>
                          <small className="text-muted">Plot Number</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="property-description mb-5">
                  <div className="row">
                    <div className="col-md-8">
                      <h5 className="h6 mb-2">Offer Description:</h5>
                      <p className="mb-4">{offer.description}</p>

                      {offer?.startDate && (
                        <div className="mt-3">
                          <h5 className="h6 mb-2">Start Date:</h5>
                          <p className="mb-0">
                            {new Date(offer.startDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}

                      {offer?.endDate && (
                        <div className="mt-3">
                          <h5 className="h6 mb-2">End Date:</h5>
                          <p className="mb-0">
                            {new Date(offer.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}

                      {offer?.propertyType === "PLOT" && layoutName && (
                        <div className="mt-3">
                          <h5 className="h6 mb-2">Layout:</h5>
                          <p className="mb-0">{layoutName}</p>
                        </div>
                      )}

                      {offer?.propertyType === "PLOT" &&
                        property?.acquisitionCost && (
                          <div className="mt-3">
                            <h5 className="h6 mb-2">Acquisition Cost:</h5>
                            <p className="mb-0">
                              {formatCurrency(property.acquisitionCost)}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {offer?.propertyType === "HOUSE" && property?.features && (
                  <div className="property-features-list mb-5">
                    <h4 className="h5 mb-3">Property Features</h4>
                    <div
                      className="row g-1"
                      dangerouslySetInnerHTML={createMarkup(property.features)}
                    />
                  </div>
                )}

                {offer?.propertyType === "PLOT" && (
                  <div className="property-features-list mb-5">
                    <h4 className="h5 mb-3">Plot Features</h4>
                    <div className="row g-1">
                      <div className="col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <strong>Plot Size:</strong> {property?.sizeSqm} SQM
                          </li>
                          <li>
                            <strong>Location Type:</strong>{" "}
                            {property?.locationType}
                          </li>
                          <li>
                            <strong>Plot Number:</strong> {property?.plotNumber}
                          </li>
                          <li>
                            <strong>Acquisition Type:</strong>{" "}
                            {property?.acquisitionType}
                          </li>
                          <li>
                            <strong>Documentation Fee:</strong>{" "}
                            {formatCurrency(property?.documentationFee || 0)}
                          </li>
                          <li>
                            <strong>Development Fee:</strong>{" "}
                            {formatCurrency(property?.developmentFee || 0)}
                          </li>
                        </ul>
                      </div>
                      {/* <div className="col-md-6">
                        <ul className="list-unstyled">
                          
                      
                        </ul>
                      </div> */}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="property-sidebar">
                <div className="property-price-card bg-light p-4 rounded mb-4">
                  <h4 className="h5 mb-3">
                    {priceInfo.hasDiscount ? (
                      <>
                        <del className="text-muted me-2 small">
                          {formatCurrency(priceInfo.originalPrice)}
                        </del>
                        <span className="text-danger fs-5">
                          {formatCurrency(priceInfo.discountedPrice)}
                        </span>
                      </>
                    ) : (
                      <span className="fs-4">
                        {formatCurrency(priceInfo.originalPrice)}
                      </span>
                    )}
                  </h4>

                  {offer?.discountPercentage &&
                    offer.discountPercentage > 0 && (
                      <p className="text-success mb-3">
                        <i className="fas fa-tag me-2"></i>
                        {offer.discountPercentage}% OFF
                      </p>
                    )}

                  <p className="text-muted mb-4">
                    <i className="fas fa-info-circle me-2"></i>
                    {property?.status === "AVAILABLE"
                      ? "Available"
                      : "Not Available"}
                  </p>

                  <div className="property-actions d-flex flex-column gap-3 mb-3">
                    <button
                      className="btn-reservation w-100"
                      disabled={property?.status !== "AVAILABLE"}
                      onClick={() => setShowInspectionModal(true)}
                    >
                      <i className="fas fa-search me-2"></i>
                      {property?.status === "AVAILABLE"
                        ? "Request Inspection"
                        : "Not Available"}
                    </button>

                    <Link
                      to="/contact"
                      className="btn btn-contact w-100 text-decoration-none"
                    >
                      <i className="fas fa-phone me-2"></i> Contact Us
                    </Link>
                  </div>

                  <div className="property-meta mt-4 pt-3 border-top">
                    <ul className="list-unstyled mb-0">
                      <li className="d-flex justify-content-between py-2">
                        <span>Offer Code:</span>
                        <strong>{offer.code}</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Property Type:</span>
                        <strong>{getPropertyType()}</strong>
                      </li>
                      {offer?.propertyType === "PLOT" &&
                        property?.plotNumber && (
                          <li className="d-flex justify-content-between py-2">
                            <span>Plot Number:</span>
                            <strong>{property.plotNumber}</strong>
                          </li>
                        )}
                      {offer?.propertyType === "PLOT" &&
                        property?.blockNumber && (
                          <li className="d-flex justify-content-between py-2">
                            <span>Block Number:</span>
                            <strong>{property.blockNumber}</strong>
                          </li>
                        )}
                      {offer?.propertyType === "PLOT" && layoutName && (
                        <li className="d-flex justify-content-between py-2">
                          <span>Layout:</span>
                          <strong>{layoutName}</strong>
                        </li>
                      )}
                      <li className="d-flex justify-content-between py-2">
                        <span>Size:</span>
                        <strong>{property?.sizeSqm} sqm</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Status:</span>
                        <strong
                          className={
                            property?.status === "AVAILABLE"
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {property?.status || "Available"}
                        </strong>
                      </li>
                      {offer?.startDate && (
                        <li className="d-flex justify-content-between py-2">
                          <span>Start Date:</span>
                          <strong>
                            {new Date(offer.startDate).toLocaleDateString()}
                          </strong>
                        </li>
                      )}
                      {offer?.endDate && (
                        <li className="d-flex justify-content-between py-2">
                          <span>End Date:</span>
                          <strong>
                            {new Date(offer.endDate).toLocaleDateString()}
                          </strong>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                {offer?.propertyType === "PLOT" &&
                  property?.locationType === "ESTATE" &&
                  property?.estate && (
                    <div className="location-info bg-light p-4 rounded mb-4 mt-4">
                      <h4 className="h5 mb-3">Estate Information</h4>
                      {property.estate.thumbnail && (
                        <div className="location-thumbnail mb-3">
                          <img
                            src={property.estate.thumbnail}
                            alt={property.estate.name}
                            className="img-fluid rounded"
                            style={{
                              height: "150px",
                              width: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <h5 className="h6">{property.estate.name}</h5>
                      <p className="small text-muted mb-3">
                        {property.estate.description}
                      </p>

                      {property.estate.features && (
                        <div className="location-features">
                          <h6 className="small fw-bold mb-2">
                            Estate Features:
                          </h6>
                          <div
                            className="row g-1 small"
                            dangerouslySetInnerHTML={createMarkup(
                              property.estate.features
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {offer?.propertyType === "PLOT" &&
                  property?.locationType === "LAYOUT" &&
                  property?.layout && (
                    <div className="location-info bg-light p-4 rounded mb-4 mt-4">
                      <h4 className="h5 mb-3">Layout Information</h4>
                      {property.layout.thumbnail && (
                        <div className="location-thumbnail mb-3">
                          <img
                            src={property.layout.thumbnail}
                            alt={property.layout.name}
                            className="img-fluid rounded"
                            style={{
                              height: "150px",
                              width: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <h5 className="h6">{property.layout.name}</h5>
                      <p
                        className="small text-muted mb-3"
                        dangerouslySetInnerHTML={{
                          __html:
                            property.layout.description ||
                            "No description available.",
                        }}
                      />

                      {property.layout.features && (
                        <div className="location-features">
                          <h6 className="small fw-bold mb-2">
                            Layout Features:
                          </h6>
                          <div
                            className="row g-1 small"
                            dangerouslySetInnerHTML={createMarkup(
                              property.layout.features
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {offer.bonusDescription && (
                  <div className="bonus-info bg-light p-4 rounded mb-4 mt-4">
                    <h4 className="h5 mb-3">Special Bonus</h4>
                    <p className="mb-0">{offer.bonusDescription}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <DetailCtaArea />

      <InspectionRequestModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        propertyId={offer?.id}
      />
    </>
  );
};

export default OfferDetails;
